"use client";

import { useRef } from "react";
import Image from "next/image";

import gsap from "gsap";
import { useGSAP } from "@gsap/react";
import { ScrollTrigger } from "gsap/dist/ScrollTrigger";
import { PortableText } from "@portabletext/react";
import { customRichTextComponent } from "@/sanity/sanityPortableText";

import { CTAButton } from "@/shadcn/ui/button";

import { PageMentoringSchema } from "@/sanity/pages/PageMentoringTemplate";

import gridBGimg from "@/public/lp2/dear/grid.svg";
import blurImg from "@/public/lp2/dear/blur.svg";
import CheckMarkGreenIcon from "@/public/mentoring/check-mark-green.svg";

if (typeof window !== "undefined") {
  gsap.registerPlugin(ScrollTrigger, useGSAP);
}

export default function PriceSection({ data }: { data: PageMentoringSchema }) {
  const containerRef = useRef<HTMLDivElement | any>(undefined);
  const content1Ref = useRef<HTMLDivElement | any>(undefined);

  useGSAP(
    () => {
      const dearTL = gsap.timeline({
        defaults: { opacity: 0, ease: "power2.inOut", duration: 0.25 },
      });

      dearTL.from(content1Ref.current.children, {
        y: "1rem",
        stagger: 0.1,
        delay: 0.25,
        scrollTrigger: {
          trigger: content1Ref.current,
          end: "center center",
          scrub: 1,
        },
      });
    },
    { scope: containerRef }
  );

  return (
    <section ref={containerRef} className="w-full overflow-x-clip">
      <div className="container-lp mx-auto pt-[4rem] lg:pt-[10rem] lg:pb-[4rem]">
        <div className="flex flex-col items-start justify-center w-full">
          <div ref={content1Ref} className="w-full max-w-[130rem] mx-auto">
            <div className="mb-[6rem] lg:mb-[8rem] w-full relative">
              {/* grid */}
              <div className="w-full lg:w-2/3 absolute left-[15%] top-[0%] -translate-y-1/2 z-[-1]">
                <Image
                  src={gridBGimg}
                  alt="grid background"
                  className="w-full"
                />
              </div>
              {/* shadow/blur */}
              <div className="w-full lg:w-1/2 absolute left-[15%] top-[0%] -translate-y-1/2 z-[-1]">
                <Image
                  src={blurImg}
                  alt="blur background"
                  className="dear-section-blur w-full"
                />
              </div>
              {/*  */}
            </div>
          </div>
          <div className="flex flex-col lg:flex-row gap-[6rem] lg:gap-[7rem] w-full max-w-[130rem] mx-auto">
            <div className="w-full h-full relative z-[2]">
              {/* border */}
              <div className="absolute z-[-1] w-2/3 h-[.125rem] bg-gradient-to-r from-[#1ECC97]/5 via-[#1ECC97] to-[#1ECC97]/0 right-[10%] top-[-.125rem]"></div>
              <div className="absolute z-[-1] w-full h-full bg-black left-1/2 -translate-x-1/2 top-1/2 -translate-y-1/2 rounded-[2rem]"></div>
              <div className="absolute z-[-2] w-[calc(100%+.25rem)] h-[calc(100%+.25rem)] bg-gradient-to-t from-[#4AC57F]/75 via-[#4AC57F]/40 to-[#4AC57F]/5 left-1/2 -translate-x-1/2 top-1/2 -translate-y-1/2 rounded-[2rem]"></div>
              {/* end border */}
              <div className="h-full rounded-[2rem] backdrop-blur-[4rem] bg-gradient-to-bl from-[#80FFD9]/20 via-[#80FFD9]/5 to-[#80FFD9]/20 px-[4rem] py-[4rem] lg:px-[6.5rem] lg:py-[6.5rem]">
                <div className="h-full flex flex-col justify-between gap-[3rem]">
                  {data?.pricingTexts1 && (
                    <PortableText
                      value={data?.pricingTexts1 ?? []}
                      components={customRichTextComponent({
                        block: {
                          h3: ({ children }) => (
                            <h3 className="gradient-title sans text-[5rem] lg:text-[5.5rem] font-[500] tracking-[-0.29rem] leading-[110%]">
                              {children}
                            </h3>
                          ),
                          normal: ({ children }) => (
                            <p className="sans text-white/60 text-[2.25rem] font-[400] leading-[140%] lg:pe-[1rem]">
                              {children}
                            </p>
                          ),
                        },
                      })}
                    />
                  )}

                  <div className="flex items-center gap-2">
                    <h2 className="gradient-title sans text-[6rem] lg:text-[6.5rem] font-[500] tracking-[-0.29rem] leading-[110%]">
                      {data?.price}
                    </h2>
                    <span className="sans text-white/40 text-[2.25rem] font-[400] leading-[140%]">
                      {" "}
                      / zwei Monate
                    </span>
                  </div>

                  <div className="w-full [&_button]:w-full">
                    {data?.pricingCTA && <CTAButton data={data?.pricingCTA} />}
                  </div>
                </div>
              </div>
            </div>
            <div className="w-full h-full">
              {data?.pricingTitle2 && (
                <PortableText
                  value={data?.pricingTitle2 ?? []}
                  components={customRichTextComponent({
                    block: {
                      h4: ({ children }) => (
                        <h4 className="gradient-title sans text-[4rem] lg:text-[4rem] font-[500] tracking-[-0.29rem] leading-[110%] lg:leading-[129%] mb-0">
                          {children}
                        </h4>
                      ),
                    },
                  })}
                />
              )}

              <ul className="mt-[4rem] lg:mt-[6rem]">
                {data?.pricingBullets &&
                  data?.pricingBullets?.map((item, index) => (
                    <li
                      key={index}
                      className="text-[2.25rem] leading-[110%] w-full flex items-start gap-3 mb-[2rem] lg:mb-[4rem]"
                    >
                      <Image
                        className="w-[3rem] h-[3rem] min-w-[3rem] min-h-[3rem]"
                        src={CheckMarkGreenIcon}
                        alt="check"
                      />
                      <div>
                        <span className="text-white font-[600]">
                          {item?.title}
                        </span>
                        {item?.text && (
                          <p className="text-white/80 font-[400] mt-2 mb-0">
                            {item?.text}
                          </p>
                        )}
                      </div>
                    </li>
                  ))}
              </ul>
            </div>
          </div>
          {/*  */}
        </div>
      </div>
    </section>
  );
}
